import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export type SubscriptionTier = 'free' | 'pro' | 'quant';

export interface TierLimits {
  strategies: number;
  brokers: number;
  canLive: boolean;
  canAutoExecute: boolean;
}

const TIER_LIMITS: Record<SubscriptionTier, TierLimits> = {
  free: { strategies: 2, brokers: 0, canLive: false, canAutoExecute: false },
  pro: { strategies: 10, brokers: 3, canLive: true, canAutoExecute: false },
  quant: { strategies: 999, brokers: 999, canLive: true, canAutoExecute: true },
};

const TIER_LABELS: Record<SubscriptionTier, string> = {
  free: 'FREE',
  pro: 'PRO',
  quant: 'QUANT',
};

export function useSubscription() {
  const { user } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['user-subscription', user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data, error } = await supabase
        .from('user_subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();
      if (error) {
        console.error('Failed to fetch subscription:', error);
        return null;
      }
      return data;
    },
    enabled: !!user,
    staleTime: 60_000,
  });

  const tier: SubscriptionTier = (data?.tier as SubscriptionTier) || 'free';
  const limits = TIER_LIMITS[tier];
  const label = TIER_LABELS[tier];

  return {
    tier,
    label,
    limits,
    isLoading,
    subscription: data,
  };
}
